import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ExclamationTriangleIcon, ClockIcon, InformationCircleIcon,
  CheckCircleIcon, ShareIcon, ChevronDownIcon, ChevronUpIcon,
  LightBulbIcon, ClipboardDocumentIcon, SparklesIcon, ArrowPathIcon,
  HeartIcon, PrinterIcon, TrashIcon,
} from '@heroicons/react/24/outline';

// ─── Urgency styling maps ─────────────────────────────────────────────────────
const urgencyColors = {
  urgent: 'bg-red-50 border-red-200 text-red-700',
  routine: 'bg-amber-50 border-amber-200 text-amber-700',
  watch: 'bg-blue-50 border-blue-200 text-blue-600',
};

const urgencyBadge = {
  urgent: 'bg-red-100 text-red-700',
  routine: 'bg-amber-100 text-amber-700',
  watch: 'bg-blue-100 text-blue-700',
};

const urgencyIcons = {
  urgent: ExclamationTriangleIcon,
  routine: ClockIcon,
  watch: InformationCircleIcon,
};

const priorityDot = {
  high: 'bg-red-400',
  medium: 'bg-amber-400',
  low: 'bg-emerald-400',
};

// ─── Custom Markdown renderers (no @tailwindcss/typography needed) ────────────
const mdComponents = {
  h1: ({ children }) => (
    <h1 className="text-xl font-bold text-slate-800 dark:text-white mb-3 mt-0 leading-snug">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[0.68rem] font-bold text-sky-600 dark:text-sky-400 uppercase tracking-[0.14em] mt-7 mb-2 pb-1.5 border-b border-sky-100 dark:border-sky-900 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-4 mb-1.5">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-medium text-slate-600 dark:text-slate-300 mt-3 mb-1">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-slate-600 dark:text-slate-300 text-[0.94rem] leading-7 mb-3 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 mb-4 space-y-1 marker:text-sky-400">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 mb-4 space-y-1 marker:text-sky-500">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-slate-600 dark:text-slate-300 text-[0.94rem] leading-6 pl-0.5">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-800 dark:text-white">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-slate-500 dark:text-slate-400">{children}</em>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-sky-300 pl-4 py-2 my-4 bg-sky-50/70 dark:bg-sky-900/20 rounded-r-lg">
      <div className="text-slate-600 dark:text-slate-300 italic text-sm leading-relaxed">{children}</div>
    </blockquote>
  ),
  hr: () => <hr className="border-slate-100 dark:border-slate-700 my-5" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-xl border border-slate-200 dark:border-slate-700">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{children}</th>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50">{children}</tr>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 text-[0.875rem]">{children}</td>
  ),
};

export default function AnalysisPage() {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(location.state || null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(!location.state);
  const [activeTab, setActiveTab] = useState('explanation');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [shareReport, setShareReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedActions, setExpandedActions] = useState(true);
  // Document Q&A state
  const [qaQuestion, setQaQuestion] = useState('');
  const [qaAnswer, setQaAnswer] = useState('');
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState('');

  useEffect(() => {
    if (!location.state) {
      api.get(`/analysis/${sessionId}`)
        .then(res => {
          setSession(res.data);
          setData({
            explanation: res.data.simplifiedExplanation,
            actionPlan: {
              urgencyFlags: res.data.urgencyFlags,
              suggestedDoctorQuestions: res.data.suggestedQuestions,
              nextSteps: res.data.actionPlan,
            },
            quiz: res.data.comprehensionQuiz,
            labValues: res.data.extractedData,
          });
          if (res.data.comprehensionScore !== null) {
            setQuizSubmitted(true);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [sessionId, location.state]);

  const handleQuizSubmit = async () => {
    try {
      const res = await api.post(`/analysis/${sessionId}/quiz-submit`, { responses: quizAnswers });
      setQuizResults(res.data);
      setQuizSubmitted(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/analysis/${sessionId}/generate-report`);
      setShareToken(res.data.shareToken);
      setShareReport(res.data.report);
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/share/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-400">Loading analysis…</div>;
  }

  if (!data) {
    return <div className="flex items-center justify-center h-64 text-red-400">Analysis not found.</div>;
  }

  const { explanation, actionPlan, quiz, labValues } = data;
  const abnormalValues = (labValues || []).filter(v => v.status && v.status !== 'normal' && v.status !== 'unknown');
  const reportId = data?.labReportId || session?.labReportId;
  const isSubscriber = user?.subscriptionStatus === 'active';

  const handleDocumentQA = async () => {
    if (!reportId || !qaQuestion.trim()) return;
    setQaLoading(true);
    setQaError('');
    setQaAnswer('');
    try {
      const res = await api.post('/premium/document-qa', { reportId, question: qaQuestion.trim() });
      setQaAnswer(res.data.answer);
    } catch (err) {
      setQaError(err.response?.data?.error || 'Could not get an answer. Please try again.');
    } finally {
      setQaLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this report and its analysis? This cannot be undone.')) return;
    try {
      await api.delete(`/analysis/${sessionId}`);
      navigate('/app/dashboard');
    } catch {
      alert('Could not delete this report. Please try again.');
    }
  };

  const TABS = [
    { id: 'explanation', label: 'Explanation' },
    { id: 'action', label: 'Action Plan' },
    { id: 'quiz', label: 'Comprehension Check' },
    { id: 'share', label: 'Share with Provider' },
    ...(isSubscriber && reportId ? [{ id: 'ask', label: '✦ Ask Questions' }] : []),
  ];

  // Derived action plan flags
  const hasUrgencyFlags = actionPlan?.urgencyFlags?.length > 0;
  const hasNextSteps = actionPlan?.nextSteps?.length > 0;
  const hasDoctorQuestions = actionPlan?.suggestedDoctorQuestions?.length > 0;
  const hasLifestyle = actionPlan?.lifestyleRecommendations?.length > 0;
  const hasAnyActionContent = hasUrgencyFlags || hasNextSteps || hasDoctorQuestions || hasLifestyle;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Your Lab Results Explained</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Personalized to your language and literacy level.</p>
        </div>
        <button
          onClick={handleDelete}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 transition-colors text-sm flex-shrink-0"
          title="Delete this report"
        >
          <TrashIcon className="w-4 h-4" />
          Delete
        </button>
      </div>

      {/* Abnormal summary badges */}
      {abnormalValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {abnormalValues.map((v, i) => (
            <span
              key={i}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                v.status?.includes('critical') ? 'bg-red-50 border-red-200 text-red-700' :
                v.status === 'high' || v.status === 'low' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                'bg-slate-50 border-slate-200 text-slate-600'
              }`}
            >
              {v.testName}: {v.rawValue} <span className="opacity-60">({v.status})</span>
            </span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
        <div className="flex gap-0 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB: Explanation ──────────────────────────────────────────────────── */}
      {activeTab === 'explanation' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          {/* Banner */}
          <div className="bg-gradient-to-r from-sky-50 to-indigo-50 dark:from-sky-900/30 dark:to-indigo-900/30 border-b border-slate-100 dark:border-slate-700 px-6 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center flex-shrink-0">
              <SparklesIcon className="w-4 h-4 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">AI-Generated Plain-Language Explanation</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Not a medical diagnosis — always confirm findings with your healthcare provider.</p>
            </div>
          </div>
          <div className="px-6 py-6">
            {explanation ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {explanation}
              </ReactMarkdown>
            ) : (
              <p className="text-slate-400 text-sm text-center py-8">No explanation available for this session.</p>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: Action Plan ─────────────────────────────────────────────────── */}
      {activeTab === 'action' && (
        <div className="space-y-5">

          {/* Priority items */}
          <div>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 text-slate-400" />
              Priority Items
            </h2>
            {hasUrgencyFlags ? (
              <div className="space-y-3">
                {actionPlan.urgencyFlags.map((flag, i) => {
                  // Support both new format {testName,urgency,reason,action} and old format {name,status,message}
                  const flagName = flag.testName || flag.name || 'Lab Value';
                  const flagUrgency = flag.urgency || (flag.status === 'high' || flag.status === 'critical' ? 'urgent' : flag.status === 'low' ? 'watch' : 'routine');
                  const flagReason = flag.reason || flag.message || '';
                  const flagAction = flag.action || '';
                  const Icon = urgencyIcons[flagUrgency] || InformationCircleIcon;
                  return (
                    <div key={i} className={`flex gap-3 p-4 rounded-xl border ${urgencyColors[flagUrgency] || 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm">{flagName}</p>
                          <span className={`text-[0.65rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${urgencyBadge[flagUrgency] || 'bg-slate-100 text-slate-600'}`}>
                            {flagUrgency}
                          </span>
                        </div>
                        <p className="text-sm opacity-90 leading-snug">{flagReason}</p>
                        {flagAction && (
                          <p className="text-sm font-medium mt-2 flex items-center gap-1.5 opacity-90">
                            <span className="opacity-50">→</span> {flagAction}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-700">No urgent items flagged</p>
                  <p className="text-xs text-emerald-600 mt-0.5 opacity-80">Your results did not trigger any immediate concerns. Review the next steps below.</p>
                </div>
              </div>
            )}
          </div>

          {/* Next steps */}
          {hasNextSteps && (
            <div>
              <button
                onClick={() => setExpandedActions(x => !x)}
                className="w-full flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                <ClipboardDocumentIcon className="w-4 h-4 text-slate-400" />
                Next Steps
                <span className="text-xs font-normal text-slate-400 ml-1">({actionPlan.nextSteps.length})</span>
                <span className="ml-auto">
                  {expandedActions ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                </span>
              </button>
              {expandedActions && (
                <div className="space-y-2">
                  {actionPlan.nextSteps.map((step, i) => {
                    // Support both new format {priority,step,timeframe} and old format (plain string)
                    const stepText = typeof step === 'string' ? step : step.step;
                    const stepPriority = typeof step === 'string' ? null : step.priority;
                    const stepTimeframe = typeof step === 'string' ? null : step.timeframe;
                    return (
                      <div key={i} className="flex gap-3 bg-white border border-slate-200 rounded-xl p-3.5 hover:border-slate-300 transition-colors">
                        <div className="flex flex-col items-center pt-1 flex-shrink-0">
                          <span className={`w-2.5 h-2.5 rounded-full ${priorityDot[stepPriority] || 'bg-slate-300'}`} />
                          {i < actionPlan.nextSteps.length - 1 && <div className="w-px flex-1 bg-slate-100 mt-1" />}
                        </div>
                        <div className="pb-1">
                          <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">{stepText}</p>
                          {stepTimeframe && (
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              <ClockIcon className="w-3 h-3" />
                              {stepTimeframe}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Doctor questions */}
          {hasDoctorQuestions && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                <LightBulbIcon className="w-4 h-4 text-amber-400" />
                Questions to Ask Your Doctor
              </h2>
              <div className="bg-amber-50 border border-amber-100 rounded-xl overflow-hidden">
                <ul className="divide-y divide-amber-100">
                  {actionPlan.suggestedDoctorQuestions.map((q, i) => (
                    <li key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-amber-100/40 transition-colors">
                      <span className="w-5 h-5 rounded-full bg-amber-200 text-amber-700 text-[0.65rem] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm text-amber-900 leading-snug">{q}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Lifestyle recommendations */}
          {hasLifestyle && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                <HeartIcon className="w-4 h-4 text-emerald-500" />
                Lifestyle Recommendations
              </h2>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl overflow-hidden">
                <ul className="divide-y divide-emerald-100">
                  {actionPlan.lifestyleRecommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-100/40 transition-colors">
                      <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-emerald-900 leading-snug">{rec}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!hasAnyActionContent && (
            <div className="text-center py-12 text-slate-400">
              <CheckCircleIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No action plan data available for this analysis.</p>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Comprehension Check ─────────────────────────────────────────── */}
      {activeTab === 'quiz' && (
        <div className="space-y-5">
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 flex items-start gap-3">
            <SparklesIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sky-800 text-sm">Comprehension Check</p>
              <p className="mt-0.5 text-sm text-sky-600 opacity-80">Answer these questions to verify you understood your results. There are no wrong answers — this is for your benefit.</p>
            </div>
          </div>

          {(!quiz?.questions || quiz.questions.length === 0) ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircleIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No comprehension check available for this session.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {quiz.questions.map((q, qIdx) => (
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
                  <div className="px-5 pt-5 pb-4">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="w-6 h-6 rounded-full bg-sky-100 dark:bg-sky-900/50 text-sky-600 dark:text-sky-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {qIdx + 1}
                      </span>
                      <p className="font-medium text-slate-800 dark:text-white text-sm leading-snug">{q.question}</p>
                    </div>
                    <div className="space-y-2 ml-9">
                      {q.options.map((opt) => {
                        const letter = opt[0];
                        const optText = opt.replace(/^[A-Z]\.\s*/, '');
                        const isSelected = quizAnswers[q.id] === letter;
                        const isCorrect = quizSubmitted && letter === q.correctAnswer;
                        const isWrong = quizSubmitted && isSelected && letter !== q.correctAnswer;

                        return (
                          <button
                            key={opt}
                            disabled={quizSubmitted}
                            onClick={() => !quizSubmitted && setQuizAnswers(a => ({ ...a, [q.id]: letter }))}
                            className={`w-full text-left px-3.5 py-2.5 rounded-lg border text-sm transition-all flex items-center gap-2.5 ${
                              isCorrect ? 'bg-emerald-50 border-emerald-300 text-emerald-700' :
                              isWrong ? 'bg-red-50 border-red-300 text-red-700' :
                              isSelected ? 'bg-sky-50 border-sky-400 text-sky-700' :
                              'border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                            }`}
                          >
                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[0.6rem] font-bold flex-shrink-0 ${
                              isCorrect ? 'bg-emerald-500 border-emerald-500 text-white' :
                              isWrong ? 'bg-red-400 border-red-400 text-white' :
                              isSelected ? 'bg-sky-500 border-sky-500 text-white' :
                              'border-slate-300 text-slate-400'
                            }`}>
                              {isCorrect ? '✓' : isWrong ? '✗' : letter}
                            </span>
                            <span className="leading-snug">{optText}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {quizSubmitted && q.explanation && (
                    <div className="border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-5 py-3">
                      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        <span className="font-semibold text-slate-600 dark:text-slate-300">Explanation: </span>{q.explanation}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              {!quizSubmitted ? (
                <button
                  onClick={handleQuizSubmit}
                  disabled={Object.keys(quizAnswers).length < quiz.questions.length}
                  className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-200 disabled:text-sky-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  {Object.keys(quizAnswers).length < quiz.questions.length
                    ? `Answer ${quiz.questions.length - Object.keys(quizAnswers).length} more question${quiz.questions.length - Object.keys(quizAnswers).length !== 1 ? 's' : ''} to submit`
                    : 'Submit Answers'}
                </button>
              ) : (
                <div className={`rounded-xl border p-5 flex items-start gap-4 ${
                  (quizResults?.score ?? session?.comprehensionScore ?? 0) >= 75
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    (quizResults?.score ?? session?.comprehensionScore ?? 0) >= 75 ? 'bg-emerald-100' : 'bg-amber-100'
                  }`}>
                    <p className={`text-lg font-bold ${
                      (quizResults?.score ?? session?.comprehensionScore ?? 0) >= 75 ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {quizResults?.score ?? session?.comprehensionScore ?? '–'}%
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">
                      {(quizResults?.score ?? session?.comprehensionScore ?? 0) >= 75 ? 'Great job!' : 'Keep learning!'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1 leading-snug">
                      {(quizResults?.score ?? session?.comprehensionScore ?? 0) >= 75
                        ? "You have a solid grasp of your results. You're ready to discuss them with your care team."
                        : 'Review the Explanation tab and discuss any unclear points with your provider — that\'s exactly what they\'re there for.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Share with Provider ──────────────────────────────────────────── */}
      {activeTab === 'share' && (
        <div className="space-y-5">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-start gap-3">
            <ShareIcon className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-indigo-800 text-sm">Share with Your Care Team</p>
              <p className="text-sm text-indigo-600 opacity-80 mt-0.5 leading-snug">
                Generate a one-page clinical summary your doctor can review — including your questions and comprehension score.
              </p>
            </div>
          </div>

          {!shareReport ? (
            <button
              onClick={handleGenerateReport}
              disabled={generating}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2.5"
            >
              {generating ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Generating your provider summary…
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="w-5 h-5" />
                  Generate Provider Summary
                </>
              )}
            </button>
          ) : (
            <div className="space-y-4">
              {/* Share link row */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                <input
                  readOnly
                  value={`${window.location.origin}/share/${shareToken}`}
                  className="flex-1 bg-transparent text-sm text-slate-600 outline-none min-w-0 truncate"
                />
                <button
                  onClick={copyShareLink}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  <ShareIcon className="w-3.5 h-3.5" />
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <p className="text-xs text-slate-400 -mt-2 px-1">
                Anyone with this link can view your provider summary. The link does not expire.
              </p>

              {/* Report preview */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-slate-100 px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClipboardDocumentIcon className="w-4 h-4 text-indigo-500" />
                    <p className="text-sm font-semibold text-slate-700">Provider Summary Preview</p>
                  </div>
                  <button
                    onClick={() => window.print()}
                    className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <PrinterIcon className="w-3.5 h-3.5" />
                    Print
                  </button>
                </div>
                <div className="px-6 py-6 max-h-[32rem] overflow-y-auto">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {shareReport}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Ask Questions (premium) ─────────────────────────────────────── */}
      {activeTab === 'ask' && isSubscriber && reportId && (
        <div className="space-y-5">
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-start gap-2">
            <SparklesIcon className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-sky-700">
              Ask any question about this specific report. Answers are grounded in your actual lab data — not generic information.
            </p>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={qaQuestion}
              onChange={e => setQaQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleDocumentQA(); }}
              placeholder="e.g. What does my high LDL mean? Is my kidney function normal?"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
            <button
              onClick={handleDocumentQA}
              disabled={qaLoading || !qaQuestion.trim()}
              className="bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
            >
              {qaLoading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <SparklesIcon className="w-4 h-4" />}
              Ask
            </button>
          </div>
          {qaError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{qaError}</p>
          )}
          {qaAnswer && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Answer</p>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {qaAnswer}
              </ReactMarkdown>
              <p className="text-xs text-slate-400 mt-4 pt-3 border-t border-slate-100">
                Grounded in your lab report data. Always verify with your healthcare provider.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
